/**
 * Account settings dialog component.
 *
 * Allows users (both guest and authenticated) to manage their account,
 * preferences, and data. Displayed as a dialog overlay on the home page.
 */

import {
  Download,
  Loader2,
  LogOut,
  Monitor,
  Moon,
  Music,
  Sun,
  Trash2,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';

import type { ThemePreference } from '@idle-depths/shared';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { getVanillaClient } from '@/lib/trpc';
import { useStores } from '@/stores/StoreContext';

interface AccountDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when the dialog is closed */
  onClose: () => void;
}

/**
 * Account settings dialog content.
 */
const AccountDialogContent = observer(function AccountDialogContent({
  onClose,
}: {
  onClose: () => void;
}): React.ReactElement | null {
  const { authStore, preferencesStore } = useStores();
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // If no server user, show nothing (dialog shouldn't be open)
  if (!authStore.hasServerUser || !authStore.user) {
    return null;
  }

  const user = authStore.user;
  const initials =
    user.displayName
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) ?? 'U';

  const handleExportData = async (): Promise<void> => {
    setExporting(true);
    try {
      const client = getVanillaClient();
      const data = await client.user.exportData.query();

      // Download as JSON file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const dateStr = new Date().toISOString().split('T')[0];
      a.download = `idle-depths-export-${dateStr ?? 'unknown'}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export data:', error);
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async (): Promise<void> => {
    setDeleting(true);
    const result = await authStore.deleteAccount();

    if (result.success) {
      onClose();
    } else {
      setDeleting(false);
    }
  };

  const handleLogout = async (): Promise<void> => {
    await authStore.logout();
    onClose();
  };

  const onLogoutClick = (): void => {
    void handleLogout();
  };

  const onExportClick = (): void => {
    void handleExportData();
  };

  const onDeleteClick = (): void => {
    void handleDeleteAccount();
  };

  return (
    <div className="space-y-6 text-sm">
      {/* Profile Section */}
      <div className="flex items-center gap-4">
        <Avatar className="h-14 w-14">
          {user.avatarUrl && <AvatarImage src={user.avatarUrl} alt={user.displayName ?? 'User'} />}
          <AvatarFallback className="text-lg">{initials}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{user.displayName ?? 'User'}</p>
          {user.email && <p className="text-sm text-muted-foreground truncate">{user.email}</p>}
        </div>
        <Button variant="outline" size="sm" onClick={onLogoutClick}>
          <LogOut className="h-4 w-4 mr-2" />
          Sign Out
        </Button>
      </div>

      <Separator />

      {/* Preferences Section */}
      <div className="space-y-4">
        <h3 className="text-base font-semibold text-biolume-cyan">Preferences</h3>

        {/* Theme */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sun className="h-4 w-4 text-muted-foreground" />
            <Label>Theme</Label>
          </div>
          <Select
            value={preferencesStore.theme}
            onValueChange={(value) => {
              preferencesStore.setTheme(value as ThemePreference);
            }}
          >
            <SelectTrigger className="w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="light">
                <div className="flex items-center gap-2">
                  <Sun className="h-3 w-3" />
                  Light
                </div>
              </SelectItem>
              <SelectItem value="dark">
                <div className="flex items-center gap-2">
                  <Moon className="h-3 w-3" />
                  Dark
                </div>
              </SelectItem>
              <SelectItem value="system">
                <div className="flex items-center gap-2">
                  <Monitor className="h-3 w-3" />
                  System
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Music Volume */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Music className="h-4 w-4 text-muted-foreground" />
              <Label>Music</Label>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground w-8 text-right">
                {preferencesStore.musicVolume}%
              </span>
              <Switch
                checked={preferencesStore.musicEnabled}
                onCheckedChange={preferencesStore.setMusicEnabled.bind(preferencesStore)}
              />
            </div>
          </div>
          <Slider
            value={[preferencesStore.musicVolume]}
            onValueChange={(values: number[]) => {
              preferencesStore.setMusicVolume(values[0] ?? 0);
            }}
            max={100}
            step={1}
            disabled={!preferencesStore.musicEnabled}
            className="py-1"
          />
        </div>

        {/* Sound Effects */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {preferencesStore.soundEnabled ? (
                <Volume2 className="h-4 w-4 text-muted-foreground" />
              ) : (
                <VolumeX className="h-4 w-4 text-muted-foreground" />
              )}
              <Label>Sound Effects</Label>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground w-8 text-right">
                {preferencesStore.soundEffectsVolume}%
              </span>
              <Switch
                checked={preferencesStore.soundEnabled}
                onCheckedChange={preferencesStore.setSoundEnabled.bind(preferencesStore)}
              />
            </div>
          </div>
          <Slider
            value={[preferencesStore.soundEffectsVolume]}
            onValueChange={(values: number[]) => {
              preferencesStore.setSoundEffectsVolume(values[0] ?? 0);
            }}
            max={100}
            step={1}
            disabled={!preferencesStore.soundEnabled}
            className="py-1"
          />
        </div>
      </div>

      <Separator />

      {/* Privacy & Consent */}
      <div className="space-y-4">
        <h3 className="text-base font-semibold text-biolume-cyan">Privacy & Consent</h3>

        <div className="flex items-center justify-between">
          <div>
            <Label>Analytics</Label>
            <p className="text-xs text-muted-foreground">Help improve the game</p>
          </div>
          <Switch
            checked={preferencesStore.analyticsConsent}
            onCheckedChange={preferencesStore.setAnalyticsConsent.bind(preferencesStore)}
          />
        </div>

        <div className="flex items-center justify-between">
          <div>
            <Label>Marketing Emails</Label>
            <p className="text-xs text-muted-foreground">Receive updates & news</p>
          </div>
          <Switch
            checked={preferencesStore.marketingConsent}
            onCheckedChange={preferencesStore.setMarketingConsent.bind(preferencesStore)}
          />
        </div>
      </div>

      <Separator />

      {/* Data Management */}
      <div className="space-y-4">
        <h3 className="text-base font-semibold text-biolume-cyan">Data Management</h3>

        <Button
          variant="outline"
          onClick={onExportClick}
          disabled={exporting}
          className="w-full justify-start"
          size="sm"
        >
          {exporting ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Download className="mr-2 h-4 w-4" />
          )}
          Export My Data (GDPR)
        </Button>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="outline"
              className="w-full justify-start border-destructive/50 text-destructive hover:bg-destructive/10 hover:text-destructive"
              size="sm"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete Account
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete your account and remove
                all your data from our servers, including:
                <ul className="mt-2 list-inside list-disc">
                  <li>Your profile information</li>
                  <li>All game progress and characters</li>
                  <li>Any purchases and entitlements</li>
                </ul>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={onDeleteClick}
                disabled={deleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete Account'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
});

/**
 * Account settings dialog.
 */
export const AccountDialog = observer(function AccountDialog({
  open,
  onClose,
}: AccountDialogProps): React.ReactElement {
  const handleOpenChange = (isOpen: boolean): void => {
    if (!isOpen) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle className="text-xl">Account Settings</DialogTitle>
          <DialogDescription>Manage your account and preferences</DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-[calc(85vh-120px)] px-6 pb-6">
          <AccountDialogContent onClose={onClose} />
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
});
